import { useState, useMemo } from 'react';
import {
    Button,
    TextField,
    InputAdornment,
    Chip,
    Tooltip,
    IconButton,
    Badge,
    Collapse,
    Alert,
    Snackbar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import FilterListIcon from '@mui/icons-material/FilterList';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const ClaimsManager = ({ claims, onSave, onCancel, isSaving = false }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [expandedCategories, setExpandedCategories] = useState({});
    const [localClaims, setLocalClaims] = useState(claims);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    // Group claims by category
    const categories = useMemo(() => {
        const categoryMap = {
            'Master': [],
            'Transaction': [],
            'Accounts': [],
            'Reports': [],
            'Administration': []
        };

        localClaims.forEach((claim, index) => {
            const categoryIndex = Math.floor(index / 5);
            const categoryNames = Object.keys(categoryMap);
            if (categoryIndex < categoryNames.length) {
                categoryMap[categoryNames[categoryIndex]].push({ ...claim, originalIndex: index });
            }
        });

        return categoryMap;
    }, [localClaims]);

    // Filter claims based on search and filter
    const filteredCategories = useMemo(() => {
        const filtered = {};
        
        Object.entries(categories).forEach(([category, claims]) => {
            const filteredClaims = claims.filter(claim => {
                const matchesSearch = claim.ClaimValue.toLowerCase().includes(searchTerm.toLowerCase());
                const matchesFilter = 
                    selectedFilter === 'all' ||
                    (selectedFilter === 'selected' && claim.IsSelected) ||
                    (selectedFilter === 'unselected' && !claim.IsSelected);
                
                return matchesSearch && matchesFilter;
            });

            if (filteredClaims.length > 0) {
                filtered[category] = filteredClaims;
            }
        });

        return filtered;
    }, [categories, searchTerm, selectedFilter]);

    // Statistics
    const stats = useMemo(() => {
        const total = localClaims.length;
        const selected = localClaims.filter(c => c.IsSelected).length;
        return { total, selected, unselected: total - selected };
    }, [localClaims]);

    const handleToggleClaim = (originalIndex) => {
        const updated = [...localClaims];
        updated[originalIndex].IsSelected = !updated[originalIndex].IsSelected;
        setLocalClaims(updated);
    };

    const handleToggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const handleSelectAllInCategory = (category) => {
        const updated = [...localClaims];
        categories[category].forEach(claim => {
            updated[claim.originalIndex].IsSelected = true;
        });
        setLocalClaims(updated);
    };

    const handleDeselectAllInCategory = (category) => {
        const updated = [...localClaims];
        categories[category].forEach(claim => {
            updated[claim.originalIndex].IsSelected = false;
        });
        setLocalClaims(updated);
    };

    const handleSelectAll = () => {
        const updated = localClaims.map(claim => ({ ...claim, IsSelected: true }));
        setLocalClaims(updated);
    };

    const handleDeselectAll = () => {
        const updated = localClaims.map(claim => ({ ...claim, IsSelected: false }));
        setLocalClaims(updated);
    };

    const handleSave = () => {
        onSave(localClaims);
        setShowSuccessMessage(true);
    };

    const getCategoryIcon = (category) => {
        const icons = {
            'Master': '📋',
            'Transaction': '💳',
            'Accounts': '👤',
            'Reports': '📊',
            'Administration': '⚙️'
        };
        return icons[category] || '📄';
    };

    return (
        <div style={{
            background: 'white',
            padding: '0',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
                paddingBottom: '10px',
                padding: '16px 16px 10px 16px',
                borderBottom: '2px solid #f0f0f0'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                        padding: '8px',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <SecurityIcon style={{ fontSize: '20px', color: 'white' }} />
                    </div>
                    <div>
                        <h2 style={{
                            margin: 0,
                            fontSize: '18px',
                            fontWeight: '700',
                            color: '#2d3748'
                        }}>
                            Manage User Claims
                        </h2>
                        <p style={{
                            margin: '2px 0 0 0',
                            fontSize: '12px',
                            color: '#718096'
                        }}>
                            Configure permissions and access rights
                        </p>
                    </div>
                </div>
                <IconButton onClick={onCancel} size="small">
                    <CloseIcon />
                </IconButton>
            </div>

            {/* Statistics Bar */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
                marginBottom: '12px',
                padding: '0 16px'
            }}>
                <div style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                }}>
                    <div style={{ fontSize: '18px', fontWeight: '700' }}>{stats.total}</div>
                    <div style={{ fontSize: '11px', opacity: 0.9, fontWeight: '500' }}>Total Claims</div>
                </div>
                <div style={{
                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                }}>
                    <div style={{ fontSize: '18px', fontWeight: '700' }}>{stats.selected}</div>
                    <div style={{ fontSize: '11px', opacity: 0.9, fontWeight: '500' }}>Active Claims</div>
                </div>
                <div style={{
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                }}>
                    <div style={{ fontSize: '18px', fontWeight: '700' }}>{stats.unselected}</div>
                    <div style={{ fontSize: '11px', opacity: 0.9, fontWeight: '500' }}>Inactive Claims</div>
                </div>
            </div>

            {/* Search and Filter Bar */}
            <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '10px',
                flexWrap: 'wrap',
                alignItems: 'center',
                padding: '0 16px'
            }}>
                <TextField
                    placeholder="Search claims..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon style={{ color: '#0f3460', fontSize: '18px' }} />
                            </InputAdornment>
                        ),
                        style: { fontSize: '13px', padding: '4px 8px' }
                    }}
                    style={{ flex: 1, minWidth: '180px' }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            height: '32px',
                            '& input': {
                                padding: '4px 8px',
                                fontSize: '13px'
                            }
                        }
                    }}
                />
                <div style={{ display: 'flex', gap: '6px' }}>
                    <Chip
                        label="All"
                        onClick={() => setSelectedFilter('all')}
                        color={selectedFilter === 'all' ? 'primary' : 'default'}
                        icon={<FilterListIcon style={{ fontSize: '16px' }} />}
                        size="small"
                        style={{ cursor: 'pointer', height: '28px', fontSize: '12px' }}
                    />
                    <Chip
                        label="Active"
                        onClick={() => setSelectedFilter('selected')}
                        color={selectedFilter === 'selected' ? 'success' : 'default'}
                        icon={<CheckCircleIcon style={{ fontSize: '16px' }} />}
                        size="small"
                        style={{ cursor: 'pointer', height: '28px', fontSize: '12px' }}
                    />
                    <Chip
                        label="Inactive"
                        onClick={() => setSelectedFilter('unselected')}
                        color={selectedFilter === 'unselected' ? 'error' : 'default'}
                        icon={<CancelIcon style={{ fontSize: '16px' }} />}
                        size="small"
                        style={{ cursor: 'pointer', height: '28px', fontSize: '12px' }}
                    />
                </div>
            </div>

            {/* Bulk Actions */}
            <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '12px',
                padding: '10px',
                background: '#f7fafc',
                borderRadius: '8px'
            }}>
                <Tooltip title="Select all claims">
                    <Button
                        size="small"
                        startIcon={<SelectAllIcon />}
                        onClick={handleSelectAll}
                        style={{
                            textTransform: 'none',
                            fontSize: '12px',
                            color: '#0f3460'
                        }}
                    >
                        Select All
                    </Button>
                </Tooltip>
                <Tooltip title="Deselect all claims">
                    <Button
                        size="small"
                        startIcon={<DeselectIcon />}
                        onClick={handleDeselectAll}
                        style={{
                            textTransform: 'none',
                            fontSize: '12px',
                            color: '#f5576c'
                        }}
                    >
                        Deselect All
                    </Button>
                </Tooltip>
            </div>

            {/* Claims by Category */}
            <div style={{
                maxHeight: '650px',
                overflowY: 'auto',
                marginBottom: '16px',
                padding: '4px'
            }}>
                {Object.entries(filteredCategories).length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px',
                        color: '#718096'
                    }}>
                        <InfoOutlinedIcon style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.5 }} />
                        <p style={{ margin: 0, fontSize: '14px' }}>No claims found matching your criteria</p>
                    </div>
                ) : (
                    Object.entries(filteredCategories).map(([category, claims]) => {
                        const isExpanded = expandedCategories[category] !== false;
                        const categoryStats = {
                            total: claims.length,
                            selected: claims.filter(c => c.IsSelected).length
                        };

                        return (
                            <div key={category} style={{
                                marginBottom: '12px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '10px',
                                overflow: 'hidden',
                                background: 'white'
                            }}>
                                {/* Category Header */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '8px 12px',
                                    background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
                                    cursor: 'pointer',
                                    borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none'
                                }}
                                onClick={() => handleToggleCategory(category)}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '18px' }}>{getCategoryIcon(category)}</span>
                                        <div>
                                            <div style={{
                                                fontSize: '13px',
                                                fontWeight: '600',
                                                color: '#2d3748'
                                            }}>
                                                {category}
                                            </div>
                                            <div style={{
                                                fontSize: '10px',
                                                color: '#718096',
                                                marginTop: '1px'
                                            }}>
                                                {categoryStats.selected} of {categoryStats.total} active
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Badge
                                            badgeContent={categoryStats.selected}
                                            color="primary"
                                            max={999}
                                        >
                                            <Chip
                                                size="small"
                                                label={`${categoryStats.total} claims`}
                                                style={{ fontSize: '10px', height: '22px' }}
                                            />
                                        </Badge>
                                        <IconButton size="small" style={{ padding: '4px' }}>
                                            {isExpanded ? <ExpandLessIcon style={{ fontSize: '18px' }} /> : <ExpandMoreIcon style={{ fontSize: '18px' }} />}
                                        </IconButton>
                                    </div>
                                </div>

                                {/* Category Content */}
                                <Collapse in={isExpanded}>
                                    <div style={{ padding: '10px' }}>
                                        {/* Category Actions */}
                                        <div style={{
                                            display: 'flex',
                                            gap: '6px',
                                            marginBottom: '8px',
                                            paddingBottom: '8px',
                                            borderBottom: '1px solid #f0f0f0'
                                        }}>
                                            <Button
                                                size="small"
                                                startIcon={<CheckCircleIcon style={{ fontSize: '14px' }} />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSelectAllInCategory(category);
                                                }}
                                                style={{
                                                    textTransform: 'none',
                                                    fontSize: '11px',
                                                    color: '#11998e',
                                                    padding: '4px 8px',
                                                    minHeight: '28px'
                                                }}
                                            >
                                                Select All
                                            </Button>
                                            <Button
                                                size="small"
                                                startIcon={<CancelIcon style={{ fontSize: '14px' }} />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeselectAllInCategory(category);
                                                }}
                                                style={{
                                                    textTransform: 'none',
                                                    fontSize: '11px',
                                                    color: '#f5576c',
                                                    padding: '4px 8px',
                                                    minHeight: '28px'
                                                }}
                                            >
                                                Deselect All
                                            </Button>
                                        </div>

                                        {/* Claims List */}
                                        <div style={{
                                            display: 'grid',
                                            gap: '6px'
                                        }}>
                                            {claims.map((claim) => (
                                                <div
                                                    key={claim.originalIndex}
                                                    onClick={() => handleToggleClaim(claim.originalIndex)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '8px 12px',
                                                        background: claim.IsSelected
                                                            ? 'linear-gradient(135deg, rgba(17, 153, 142, 0.1) 0%, rgba(56, 239, 125, 0.1) 100%)'
                                                            : '#f9fafb',
                                                        borderRadius: '5px',
                                                        border: claim.IsSelected
                                                            ? '1.5px solid #11998e'
                                                            : '1.5px solid #e5e7eb',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        ':hover': {
                                                            transform: 'translateX(4px)'
                                                        }
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = 'translateX(4px)';
                                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'translateX(0)';
                                                        e.currentTarget.style.boxShadow = 'none';
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        flex: 1
                                                    }}>
                                                        <div style={{
                                                            width: '5px',
                                                            height: '5px',
                                                            borderRadius: '50%',
                                                            background: claim.IsSelected
                                                                ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                                                                : '#cbd5e0'
                                                        }} />
                                                        <span style={{
                                                            fontSize: '12px',
                                                            fontWeight: claim.IsSelected ? '600' : '500',
                                                            color: claim.IsSelected ? '#11998e' : '#4a5568'
                                                        }}>
                                                            {claim.ClaimValue}
                                                        </span>
                                                    </div>
                                                    <div style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        borderRadius: '3px',
                                                        border: claim.IsSelected ? '1.5px solid #11998e' : '1.5px solid #cbd5e0',
                                                        background: claim.IsSelected ? '#11998e' : 'white',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        transition: 'all 0.2s ease'
                                                    }}>
                                                        {claim.IsSelected && (
                                                            <CheckCircleIcon style={{
                                                                fontSize: '11px',
                                                                color: 'white'
                                                            }} />
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </Collapse>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Action Buttons */}
            <div style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end',
                paddingTop: '12px',
                borderTop: '2px solid #f0f0f0'
            }}>
                <Button
                    variant="outlined"
                    onClick={onCancel}
                    disabled={isSaving}
                    style={{
                        textTransform: 'none',
                        borderColor: '#cbd5e0',
                        color: '#4a5568',
                        padding: '8px 24px',
                        borderRadius: '8px',
                        fontWeight: '600'
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={isSaving}
                    startIcon={isSaving ? null : <SaveIcon />}
                    style={{
                        textTransform: 'none',
                        background: isSaving
                            ? '#cbd5e0'
                            : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                        color: 'white',
                        padding: '8px 24px',
                        borderRadius: '8px',
                        fontWeight: '600',
                        boxShadow: isSaving ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.4)'
                    }}
                >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
            </div>

            {/* Success Snackbar */}
            <Snackbar
                open={showSuccessMessage}
                autoHideDuration={3000}
                onClose={() => setShowSuccessMessage(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setShowSuccessMessage(false)}
                    severity="success"
                    style={{ borderRadius: '8px' }}
                >
                    Claims updated successfully!
                </Alert>
            </Snackbar>
        </div>
    );
};

export default ClaimsManager;
